import React, { useEffect, useRef, useState, useMemo } from 'react';

const COMMON_BOTS = [
  'nightbot',
  'streamelements',
  'streamstickers',
  'streamlabs',
  'moobot',
  'fossabot',
  'wizebot',
  'phantombot',
  'deepbot',
  'ankhbot',
  'xanbot',
  'vivbot',
  'coebot',
  'ohbot',
  'revlobot',
  'stay_hydrated_bot',
  'commanderroot',
  'pretzelrocks',
  'soundalerts',
  'streamholics',
  'anotherttvviewer',
  'electricallongboard',
  'football',
  'thepositivebot',
  'flannelbot'
];

const PIXEL_STAR_SHADOW = [
  '0 5px 0 0', '-5px 10px 0 0', '0 10px 0 0', '5px 10px 0 0',
  '-15px 15px 0 0', '-10px 15px 0 0', '-5px 15px 0 0', '0 15px 0 0',
  '5px 15px 0 0', '10px 15px 0 0', '15px 15px 0 0',
  '-5px 20px 0 0', '0 20px 0 0', '5px 20px 0 0', '0 25px 0 0', '0 30px 0 0',
].join(',');

function PixelStar({ color, opacity, rotateDeg }) {
  return (
    <span
      aria-hidden
      style={{
        position: 'absolute',
        top: 0,
        left: '50%',
        marginLeft: '-2.5px',
        width: 5,
        height: 5,
        color,
        background: 'currentColor',
        boxShadow: PIXEL_STAR_SHADOW,
        opacity,
        transform: `rotate(${rotateDeg}deg)`,
        transformOrigin: 'center 17px',
        imageRendering: 'pixelated',
      }}
    />
  );
}

export default function ChatUserCircles({
  users = [],
  channelName = '',
  monitorBotUsername = null,
  reservedRightWidth = 0,
  starColors = null,
  usernameColor = '#d4c4a8',
  usernameTextShadow = '1px 1px 2px rgba(0, 0, 0, 0.8)',
}) {
  const containerRef = useRef(null);
  const animationFrameRef = useRef(null);
  const [circles, setCircles] = useState([]);
  const circlesDataRef = useRef(new Map());

  const excludedUsers = useMemo(() => {
    const normalizedChannel = channelName?.toLowerCase().trim() || '';
    const excluded = new Set(COMMON_BOTS);

    if (normalizedChannel) {
      excluded.add(normalizedChannel);
    }
    if (monitorBotUsername) {
      excluded.add(monitorBotUsername.toLowerCase());
    }

    return excluded;
  }, [channelName, monitorBotUsername]);

  const filteredUsers = useMemo(() => {
    return users.filter(username => {
      const normalized = username?.toLowerCase().trim();
      if (!normalized) return false;
      if (excludedUsers.has(normalized)) return false;
      if (normalized.startsWith('justinfan')) return false;
      return true;
    });
  }, [users, excludedUsers]);

  // Initialize circles with random positions and velocities
  useEffect(() => {
    // Edge case: If no filtered users, clear all circles immediately
    if (!filteredUsers || filteredUsers.length === 0) {
      circlesDataRef.current.clear();
      setCircles([]);
      return;
    }

    if (!containerRef.current) return;

    const container = containerRef.current;
    const containerWidth = container.offsetWidth;
    const containerHeight = container.offsetHeight;
    
    // Bottom area: last 300px of screen
    const bottomAreaTop = containerHeight - 300;
    const circleRadius = 30;
    const playfieldWidth = Math.max(circleRadius * 2, containerWidth - reservedRightWidth);
    const maxCircles = Math.min(filteredUsers.length, 100);

    setCircles(prevCircles => {
      const newCircles = [];
      const currentUsernames = new Set(filteredUsers.slice(0, maxCircles));

      // Keep existing circles ONLY for users still in chat
      // This ensures circles for users who left are immediately removed
      prevCircles.forEach(circle => {
        if (currentUsernames.has(circle.username)) {
          const spd = typeof circle.speed === 'number' ? circle.speed : 0.32 + Math.random() * 0.28;
          const desiredAngle =
            typeof circle.desiredAngle === 'number'
              ? circle.desiredAngle
              : typeof circle.phase === 'number'
                ? circle.phase
                : Math.random() * Math.PI * 2;
          let vx = circle.vx;
          let vy = circle.vy;
          if (typeof vx !== 'number' || typeof vy !== 'number') {
            vx = Math.cos(desiredAngle) * spd * 0.85;
            vy = Math.sin(desiredAngle) * spd * 0.85;
          }
          newCircles.push({
            ...circle,
            speed: spd,
            desiredAngle,
            vx,
            vy,
            rotPhase: typeof circle.rotPhase === 'number' ? circle.rotPhase : Math.random() * Math.PI * 2,
            rotSpeed:
              typeof circle.rotSpeed === 'number'
                ? circle.rotSpeed
                : (Math.random() > 0.5 ? 1 : -1) * (0.000028 + Math.random() * 0.000052)
          });
          currentUsernames.delete(circle.username);
        }
        // Circles for users not in currentUsernames are NOT added to newCircles
        // This causes them to be removed from the component
      });

      // Add new circles for new users
      currentUsernames.forEach(username => {
        const spd = 0.32 + Math.random() * 0.28;
        const desiredAngle = Math.random() * Math.PI * 2;
        newCircles.push({
          username,
          x: Math.random() * (playfieldWidth - circleRadius * 2) + circleRadius,
          y: bottomAreaTop + Math.random() * (300 - circleRadius * 2) + circleRadius,
          radius: circleRadius,
          speed: spd,
          desiredAngle,
          vx: Math.cos(desiredAngle) * spd * 0.85,
          vy: Math.sin(desiredAngle) * spd * 0.85,
          rotPhase: Math.random() * Math.PI * 2,
          rotSpeed: (Math.random() > 0.5 ? 1 : -1) * (0.000028 + Math.random() * 0.000052)
        });
      });

      // Update ref IMMEDIATELY - this is the source of truth for the animation loop
      // Any circles not in this ref will not be animated
      circlesDataRef.current = new Map(newCircles.map(c => [c.username, c]));

      return newCircles;
    });
  }, [filteredUsers, reservedRightWidth]);

  // Cleanup effect: Clear circles when component unmounts
  useEffect(() => {
    return () => {
      // Cleanup on unmount
      circlesDataRef.current.clear();
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  // Animation loop - only run when there are circles
  useEffect(() => {
    if (!containerRef.current || circles.length === 0) return;

    let isRunning = true;

    const animate = () => {
      if (!isRunning || !containerRef.current) return;

      // Use circlesDataRef as source of truth - this ensures we only animate circles that still exist
      const currentCircles = Array.from(circlesDataRef.current.values());
      if (currentCircles.length === 0) {
        // No circles to animate, stop the animation loop
        return;
      }

      const container = containerRef.current;
      if (!container) return; // Defensive check
      
      const containerWidth = container.offsetWidth;
      const containerHeight = container.offsetHeight;
      const bottomAreaTop = containerHeight - 300;

      // Inertial steering: random walk on "intent" angle + velocity lags target (organic arcs, not box pong)
      const STEER = 0.054;
      const ANGLE_JITTER = 0.11;
      const TURB = 0.026;
      const REST = 0.58;
      const WALL_KICK = 0.17;
      const BOUNCE_ANGLE_NOISE = 0.48;

      const updatedCircles = currentCircles
        .filter(circle => circle && circle.username)
        .map(circle => {
        const r = circle.radius;
        const playfieldWidth = Math.max(r * 2, containerWidth - reservedRightWidth);
        const spd = typeof circle.speed === 'number' ? circle.speed : 0.46;
        let desiredAngle =
          typeof circle.desiredAngle === 'number'
            ? circle.desiredAngle
            : typeof circle.phase === 'number'
              ? circle.phase
              : Math.random() * Math.PI * 2;

        let vx = typeof circle.vx === 'number' ? circle.vx : Math.cos(desiredAngle) * spd * 0.85;
        let vy = typeof circle.vy === 'number' ? circle.vy : Math.sin(desiredAngle) * spd * 0.85;

        desiredAngle += (Math.random() - 0.5) * ANGLE_JITTER;

        const targetVx = Math.cos(desiredAngle) * spd;
        const targetVy = Math.sin(desiredAngle) * spd;

        vx += (targetVx - vx) * STEER;
        vy += (targetVy - vy) * STEER;

        vx += (Math.random() - 0.5) * TURB;
        vy += (Math.random() - 0.5) * TURB;

        const maxSp = spd * 1.38;
        const mag = Math.hypot(vx, vy);
        if (mag > maxSp) {
          vx *= maxSp / mag;
          vy *= maxSp / mag;
        }

        let newX = circle.x + vx;
        let newY = circle.y + vy;

        if (newX - r <= 0) {
          newX = r;
          vx *= -REST;
          vy += (Math.random() - 0.5) * WALL_KICK;
          desiredAngle = Math.atan2(vy, vx) + (Math.random() - 0.5) * BOUNCE_ANGLE_NOISE;
        } else if (newX + r >= playfieldWidth) {
          newX = playfieldWidth - r;
          vx *= -REST;
          vy += (Math.random() - 0.5) * WALL_KICK;
          desiredAngle = Math.atan2(vy, vx) + (Math.random() - 0.5) * BOUNCE_ANGLE_NOISE;
        }

        if (newY - r <= bottomAreaTop) {
          newY = bottomAreaTop + r;
          vy *= -REST;
          vx += (Math.random() - 0.5) * WALL_KICK;
          desiredAngle = Math.atan2(vy, vx) + (Math.random() - 0.5) * BOUNCE_ANGLE_NOISE;
        } else if (newY + r >= containerHeight) {
          newY = containerHeight - r;
          vy *= -REST;
          vx += (Math.random() - 0.5) * WALL_KICK;
          desiredAngle = Math.atan2(vy, vx) + (Math.random() - 0.5) * BOUNCE_ANGLE_NOISE;
        }

        const phase = Math.atan2(vy, vx);

        return {
          ...circle,
          x: newX,
          y: newY,
          phase,
          vx,
          vy,
          desiredAngle,
          speed: spd
        };
      });

      // Defensive: Only update if we have valid circles
      if (updatedCircles.length > 0) {
        // Update ref first (source of truth)
        circlesDataRef.current = new Map(updatedCircles.map(c => [c.username, c]));

        // Then update state to trigger re-render
        setCircles(updatedCircles);
      } else {
        // No circles left, clear everything
        circlesDataRef.current.clear();
        setCircles([]);
      }

      // Only schedule next frame if still running
      if (isRunning) {
        animationFrameRef.current = requestAnimationFrame(animate);
      }
    };

    animationFrameRef.current = requestAnimationFrame(animate);

    return () => {
      isRunning = false;
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [circles.length, reservedRightWidth]);

  if (!filteredUsers || filteredUsers.length === 0) {
    return null;
  }

  return (
    <div 
      ref={containerRef}
      className="chat-user-circles-container"
      style={{
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        height: '300px',
        pointerEvents: 'none',
        zIndex: 100,
        overflow: 'hidden'
      }}
    >
      {circles.map((circle, index) => {
        const t = performance.now();
        const twinkle =
          0.88 + 0.12 * Math.sin(t * 0.0024 + (circle.phase ?? 0) + circle.username.length);
        const rotSpeed = typeof circle.rotSpeed === 'number' ? circle.rotSpeed : 0.00004;
        const rotPhase = typeof circle.rotPhase === 'number' ? circle.rotPhase : 0;
        const spinRad = t * rotSpeed + rotPhase;
        const wobbleRad = Math.sin(t * 0.00105 + rotPhase * 1.3) * 0.11;
        const rotateDeg = (spinRad + wobbleRad) * (180 / Math.PI);
        const starColor = starColors
          ? starColors[index % starColors.length]
          : null;
        return (
        <div
          key={circle.username}
          className="chat-user-star"
          style={{
            position: 'absolute',
            left: `${circle.x - circle.radius}px`,
            top: `${circle.y - circle.radius}px`,
            width: `${circle.radius * 2}px`,
            height: `${circle.radius * 2}px`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'none',
            pointerEvents: 'none'
          }}
        >
          {starColor ? (
            <div
              style={{
                position: 'relative',
                width: 35,
                height: 35,
              }}
            >
              <PixelStar color={starColor} opacity={twinkle} rotateDeg={rotateDeg} />
            </div>
          ) : (
            <img
              src="/images/chat-user-star.png"
              alt=""
              aria-hidden
              draggable={false}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                imageRendering: 'pixelated',
                userSelect: 'none',
                opacity: twinkle,
                transform: `rotate(${rotateDeg}deg)`,
                transformOrigin: 'center center'
              }}
            />
          )}
          <span
            style={{
              position: 'absolute',
              top: '100%',
              marginTop: '5px',
              left: '50%',
              transform: 'translateX(-50%)',
              fontSize: '10px',
              fontFamily: "'Press Start 2P', monospace",
              color: usernameColor,
              textShadow: usernameTextShadow,
              whiteSpace: 'nowrap',
              pointerEvents: 'none'
            }}
          >
            {circle.username}
          </span>
        </div>
        );
      })}
    </div>
  );
}

